import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  PowerSettingsNew as PowerIcon,
  Visibility as EyeIcon
} from '@mui/icons-material'
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable
} from '@tanstack/react-table'
import B2BCatalogApi from 'api/b2b/CatalogApiB2b'
import OrbitSession from 'hooks/useCookies'

import CatalogItemsModal from './CatalogItemsModal'
import TdpAssignedModal from './TdpAssignedModal'

// Define the shape of catalog group data
type CatalogGroup = {
  catalog_group_id: string
  catalog_group_name: string
  catalog_count: number
  company_assigned_to: string
  status: boolean
  created_by: string
  created_on: string
  updated_by: string
  updated_on: string
}

interface CatalogGroupListTableProps {
  catalogGroupData?: CatalogGroup[]
  setTableData: React.Dispatch<React.SetStateAction<CatalogGroup[]>>
}

// Action dropdown component
const ActionDropdown = ({
  row,
  onStatusUpdate
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  row: any
  onStatusUpdate: (id: string, newStatus: boolean) => void
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const catalogGroup = row.original
  const token = OrbitSession.get('faberToken')
  const queryClient = useQueryClient()

  const navigate = useNavigate()

  const { mutate, isLoading } = useMutation({
    mutationFn: async (nextStatus: boolean) => {
      return B2BCatalogApi.updateCatalogGroupStatus(
        {
          id: catalogGroup.catalog_group_id.toString(), // Convert to string
          updated_by: OrbitSession.get('email') || '',
          status: nextStatus
        },
        token
      )
    },
    onSuccess: (_res, nextStatus) => {
      // Update the local state through callback
      onStatusUpdate(catalogGroup.catalog_group_id.toString(), nextStatus) // Convert to string

      // Invalidate the cache for search results
      queryClient.invalidateQueries({
        queryKey: ['catalog-groups'],
        refetchType: 'none'
      })

      toast.success(
        `Catalog Group ${nextStatus ? 'activated' : 'deactivated'} successfully`
      )
    },
    onError: () => {
      toast.error('Failed to update status')
    }
  })

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleEdit = () => {
    navigate(
      '/hq/b2b/catalog/add?mode=edit&id=' +
        catalogGroup.catalog_group_id.toString() +
        ''
    )
    handleClose()
  }

  const handleToggleStatus = () => {
    mutate(!catalogGroup.status) // Fixed: use catalogGroup.status instead of row.original.isActive
    handleClose()
  }

  return (
    <>
      <IconButton
        aria-label='more'
        onClick={handleClick}
        size='small'
        disabled={isLoading} // Disable button while loading
      >
        {isLoading ? <CircularProgress size={16} /> : <MoreVertIcon />}
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
      >
        <MenuItem onClick={handleEdit} disabled={isLoading}>
          <EditIcon sx={{ mr: 1, fontSize: 18 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleToggleStatus} disabled={isLoading}>
          <PowerIcon sx={{ mr: 1, fontSize: 18 }} />
          {catalogGroup.status ? 'Deactivate' : 'Activate'}
        </MenuItem>
      </Menu>
    </>
  )
}

// Status chip component
const StatusChip = ({ status }: { status: boolean }) => {
  return (
    <Chip
      label={status ? 'Active' : 'Inactive'}
      color={status ? 'success' : 'error'}
      size='small'
      variant='filled'
    />
  )
}
export interface CatalogItem {
  id: string
  tenant: string
  name: string
}

export default function CatalogGroupListTable({
  catalogGroupData = [],
  setTableData
}: CatalogGroupListTableProps) {
  const handleStatusUpdate = (catalogGroupId: string, newStatus: boolean) => {
    setTableData((prevState) => {
      return prevState.map((catalogGroup) => {
        if (catalogGroup.catalog_group_id.toString() === catalogGroupId) {
          return { ...catalogGroup, status: newStatus } // Fixed: update 'status' property
        }
        return catalogGroup
      })
    })
  }
  const token = OrbitSession.get('faberToken')
  const [catalogItems, setCatalogItems] = useState<CatalogItem[]>([])
  const [tdpAssigned, setTdpAssigned] = useState<string[]>([])
  const [showCatalogModal, setShowCatalogModal] = useState(false)
  const columnHelper = createColumnHelper<CatalogGroup>()

  // Catalog creation mutation
  const { mutate: getCatalogGroupItems, isLoading: isCatalogListLoading } =
    useMutation((id: string) => B2BCatalogApi.getCatalogItems(token, id), {
      onSuccess: (data) => {
        setCatalogItems(data.data)
        setShowCatalogModal(true)
      },
      onError: (error) => {
        console.error('API Error:', error)
        toast.error('Failed to create catalog')
      }
    })

  const handleCatalogItems = (id) => {
    getCatalogGroupItems(id)
  }

  const handleTdpAssignment = (names: string[]) => {
    setTdpAssigned(names)
  }

  // Define columns based on your specified headers
  const columns = [
    columnHelper.accessor('catalog_group_name', {
      header: 'Catalog Group Name',
      cell: (info) => (
        <Typography variant='body2' fontWeight='medium'>
          {info.getValue()}
        </Typography>
      )
    }),
    columnHelper.accessor('catalog_group_id', {
      header: 'Catalog Group ID',
      cell: (info) => (
        <Typography variant='body2' fontFamily='monospace'>
          {info.getValue()}
        </Typography>
      )
    }),
    columnHelper.accessor('catalog_count', {
      header: 'Catalog Count',
      cell: (info) => (
        <Typography variant='body2' fontWeight='600'>
          {info.getValue()}
          {isCatalogListLoading ? (
            <CircularProgress />
          ) : (
            <EyeIcon
              sx={{ ml: 1, fontSize: 16 }}
              onClick={() => {
                handleCatalogItems(info.row.original.catalog_group_id)
              }}
            />
          )}
        </Typography>
      )
    }),
    columnHelper.accessor('company_assigned_to', {
      header: 'Company Assigned To',
      cell: (info) => {
        const raw = info.getValue() as string | undefined | null
        if (!raw || !raw.trim()) {
          return (
            <Typography variant='body2' color='text.secondary'>
              Not assigned
            </Typography>
          )
        }
        const parts = raw.split('::').filter(Boolean)
        return (
          <Typography variant='body2'>
            {parts.length} TDP
            <EyeIcon
              sx={{ ml: 1, fontSize: 16 }}
              onClick={() => {
                handleTdpAssignment(parts)
              }}
            />
          </Typography>
        )
      }
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: (info) => <StatusChip status={info.getValue()} />
    }),
    columnHelper.accessor('created_by', {
      header: 'Created By',
      cell: (info) => <Typography variant='body2'>{info.getValue()}</Typography>
    }),
    columnHelper.accessor('created_on', {
      header: 'Created On',
      cell: (info) => (
        <Typography variant='body2' color='text.secondary'>
          {new Date(info.getValue()).toLocaleDateString()}
        </Typography>
      )
    }),
    columnHelper.accessor('updated_by', {
      header: 'Updated By',
      cell: (info) => <Typography variant='body2'>{info.getValue()}</Typography>
    }),
    columnHelper.accessor('updated_on', {
      header: 'Updated On',
      cell: (info) => (
        <Typography variant='body2' color='text.secondary'>
          {new Date(info.getValue()).toLocaleDateString()}
        </Typography>
      )
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Action',
      cell: ({ row }) => (
        <ActionDropdown row={row} onStatusUpdate={handleStatusUpdate} />
      )
    })
  ]

  const table = useReactTable({
    data: catalogGroupData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.catalog_group_id.toString(),
    enableRowSelection: false
  })

  return (
    <Box sx={{ p: 3 }}>
      <TableContainer component={Paper} elevation={1}>
        <Table>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: 'grey.50',
                      py: 2
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  sx={{ textAlign: 'center', py: 6 }}
                >
                  <Typography color='text.secondary'>
                    No catalog groups found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{
                    '&:nth-of-type(odd)': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} sx={{ py: 2.5 }}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {showCatalogModal && (
        <CatalogItemsModal
          catalogItems={catalogItems}
          setShowCatalogModal={setShowCatalogModal}
        />
      )}
      {tdpAssigned.length > 0 && (
        <TdpAssignedModal
          tdpAssigned={tdpAssigned}
          setTdpAssigned={setTdpAssigned}
        />
      )}
    </Box>
  )
}
